import { useEffect, useRef } from 'react';

/* A module-level stack of open overlay layers. Esc must close only the topmost one,
   so each layer registers itself on mount and the keydown handler checks whether it
   is currently on top before acting. */
const layerStack = [];

let nextLayerId = 1;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Registers an overlay layer: traps Tab focus inside `ref`, closes on Esc when it is
 * the topmost layer, moves focus in on open, and returns focus to whatever was focused
 * before the layer opened.
 */
export default function useLayer({ open, onClose, ref, autoFocus = true }) {
  const idRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const id = nextLayerId;
    nextLayerId += 1;
    idRef.current = id;
    layerStack.push(id);

    returnFocusRef.current = document.activeElement;

    // Block background scroll while any layer is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (autoFocus) {
      // Defer so the node exists and any entry animation has begun.
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        const first = node.querySelector(FOCUSABLE);
        if (first) first.focus();
        else node.focus();
      });
    }

    const onKeyDown = (event) => {
      if (layerStack[layerStack.length - 1] !== id) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const node = ref.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const index = layerStack.indexOf(id);
      if (index !== -1) layerStack.splice(index, 1);
      if (layerStack.length === 0) document.body.style.overflow = previousOverflow;
      const target = returnFocusRef.current;
      if (target && typeof target.focus === 'function') target.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
